// src/components/KanbanComponents.tsx
import React, { memo } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Avatar,
  useTheme,
  Tooltip,
  Skeleton,
  Badge,
  alpha,
} from "@mui/material";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Job } from "../types/job";

interface KanbanCardProps {
  id: string;
  title: string;
  applicationsCount: number;
  deadline?: string;
  reviewStarted?: string;
  closedDate?: string;
  columnId: string;
  shortlistedCount?: number;
  finalizedCount?: number;
  onClick?: () => void;
}

export const KanbanCard: React.FC<KanbanCardProps> = memo(({
  id,
  title,
  applicationsCount,
  deadline,
  reviewStarted,
  closedDate,
  columnId,
  shortlistedCount,
  finalizedCount,
  onClick,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: { sortable: { containerId: columnId } },
    disabled: columnId === "closed", // Cannot drag closed jobs
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 10,
      }
    : undefined;

  const theme = useTheme();

  // Check if deadline is close or passed
  const isDeadlineClose = () => {
    if (!deadline) return false;
    
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays <= 3 && diffDays >= 0;
  };
  
  const isDeadlinePassed = () => {
    if (!deadline) return false;
    
    const deadlineDate = new Date(deadline);
    const today = new Date();
    
    return deadlineDate < today;
  };

  const getCardBorderColor = () => {
    if (columnId === "open") {
      if (isDeadlinePassed()) return theme.palette.error.main;
      if (isDeadlineClose()) return theme.palette.warning.main;
      return theme.palette.primary.main;
    }
    if (columnId === "inReview") return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    
    // If it's already in YYYY-MM-DD format
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    // If it has time component too
    return dateString.split(" ")[0];
  };

  const renderCardContent = () => {
    switch (columnId) {
      case "open":
        return (
          <Stack spacing={1.5} sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Tooltip title={`${applicationsCount} Applications`}>
                <Chip
                  icon={<PeopleAltIcon sx={{ fontSize: '0.85rem' }} />}
                  label={applicationsCount}
                  size="small"
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.1), 
                    color: "primary.main",
                    fontWeight: "bold",
                    borderRadius: "6px",
                    '& .MuiChip-label': {
                      px: 1,
                    }
                  }}
                />
              </Tooltip>
              
              {deadline && (
                <Tooltip title={isDeadlinePassed() ? "Deadline Passed" : 
                         isDeadlineClose() ? "Deadline Approaching" : "Application Deadline"}>
                  <Chip
                    icon={<CalendarTodayIcon sx={{ fontSize: '0.85rem' }} />}
                    label={formatDate(deadline)}
                    size="small"
                    sx={{ 
                      bgcolor: isDeadlinePassed() ? alpha(theme.palette.error.main, 0.1) : 
                             isDeadlineClose() ? alpha(theme.palette.warning.main, 0.1) : 
                             alpha(theme.palette.primary.main, 0.05), 
                      color: isDeadlinePassed() ? "error.main" : 
                             isDeadlineClose() ? "warning.main" : 
                             "text.secondary",
                      borderRadius: "6px",
                      fontWeight: isDeadlinePassed() || isDeadlineClose() ? "bold" : "normal",
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Stack>
        );
      case "inReview":
        return (
          <Stack spacing={1.5} sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Total Applications">
                  <Chip
                    icon={<PeopleAltIcon sx={{ fontSize: '0.85rem' }} />}
                    label={applicationsCount}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      borderRadius: "6px",
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                </Tooltip>
                
                <Tooltip title="Shortlisted Candidates">
                  <Chip
                    icon={<PersonIcon sx={{ fontSize: '0.85rem' }} />}
                    label={shortlistedCount || 0}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.warning.main, 0.1), 
                      color: "warning.main",
                      fontWeight: "bold",
                      borderRadius: "6px",
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                </Tooltip>
              </Stack>
              
              {reviewStarted && (
                <Tooltip title="Review Started">
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '0.85rem' }} />}
                    label={formatDate(reviewStarted)}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.warning.main, 0.05), 
                      color: "text.secondary",
                      borderRadius: "6px",
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Stack>
        );
      case "closed":
        return (
          <Stack spacing={1.5} sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Total Applications">
                  <Chip
                    icon={<PeopleAltIcon sx={{ fontSize: '0.85rem' }} />}
                    label={applicationsCount}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      borderRadius: "6px",
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                </Tooltip>
                
                <Tooltip title="Finalized Candidates">
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: '0.85rem' }} />}
                    label={finalizedCount || 0}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.1), 
                      color: "success.main",
                      fontWeight: "bold",
                      borderRadius: "6px",
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                </Tooltip>
              </Stack>
              
              {closedDate && (
                <Tooltip title="Closed Date">
                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '0.85rem' }} />}
                    label={formatDate(closedDate)}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.05), 
                      color: "text.secondary",
                      borderRadius: "6px",
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Stack>
        );
      default:
        return null;
    }
  };

  // Handle click without interfering with drag operations
  const handleClick = (e: React.MouseEvent) => {
    if (!isDragging && onClick) {
      onClick();
    }
  };

  return (
    <Paper
      ref={setNodeRef}
      onClick={handleClick}
      {...listeners}
      {...attributes}
      elevation={isDragging ? 8 : 1}
      style={style}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        backgroundColor: "#ffffff", // Use hex code instead of "white"
        transition: "all 0.2s ease-in-out",
        opacity: isDragging ? 0.9 : 1,
        transform: isDragging ? "scale(1.02)" : "scale(1)",
        cursor: columnId === "closed" ? "pointer" : "grab",
        position: "relative",
        overflow: 'hidden',
        "&:before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          backgroundColor: getCardBorderColor(),
          borderTopLeftRadius: "8px",
          borderBottomLeftRadius: "8px",
        },
        ":after": isDragging ? {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: 2,
          boxShadow: `0 0 0 2px ${getCardBorderColor()}`,
          animation: "pulse 1.5s infinite",
          "@keyframes pulse": {
            "0%": { opacity: 0.6 },
            "50%": { opacity: 0.3 },
            "100%": { opacity: 0.6 },
          }
        } : {},
        ":hover": {
          boxShadow: columnId === "closed" ? 2 : 4,
          transform: columnId === "closed" ? "scale(1)" : "scale(1.01)",
        },
        ":active": {
          cursor: columnId === "closed" ? "pointer" : "grabbing",
        },
        ":focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px",
        },
      }}
      aria-roledescription={columnId === "closed" ? "Job card" : "Draggable job card"}
      aria-label={`${title} with ${applicationsCount} applicants`}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
        <Typography 
          variant="subtitle1" 
          fontWeight="bold"
          sx={{ 
            color: "text.primary", 
            flexGrow: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            fontSize: { xs: '0.95rem', sm: '1rem' },
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        
        <Tooltip title="More options">
          <Box 
            sx={{ 
              ml: 1, 
              color: "text.secondary",
              borderRadius: "50%",
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: alpha(theme.palette.action.active, 0.1),
              }
            }}
            role="button"
            aria-label="More options"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              // You can implement a menu or other actions here
            }}
          >
            <MoreVertIcon fontSize="small" />
          </Box>
        </Tooltip>
      </Box>
      
      {renderCardContent()}
    </Paper>
  );
});

KanbanCard.displayName = "KanbanCard";

export const KanbanCardSkeleton: React.FC = () => {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        backgroundColor: "#ffffff", // Use hex code instead of "white"
        borderLeft: "4px solid #e0e0e0",
      }}
    >
      <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1.5 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Skeleton variant="rounded" width={60} height={24} />
        <Skeleton variant="rounded" width={100} height={24} />
      </Box>
    </Paper>
  );
};

interface KanbanColumnProps {
  id: string;
  title: string;
  jobs: Job[];
  onCardClick?: (job: Job) => void;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = memo(({
  id,
  title,
  jobs,
  onCardClick
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    disabled: id === "closed", // We'll handle this in ManageJobs component
  });

  const theme = useTheme();

  const getColumnStyles = () => {
    switch (id) {
      case "open":
        return {
          bgColor: alpha(theme.palette.primary.main, 0.03),
          headerBg: theme.palette.primary.main,
          borderColor: theme.palette.primary.light,
          hoverBg: alpha(theme.palette.primary.main, 0.08),
          accentColor: theme.palette.primary.main,
        };
      case "inReview":
        return {
          bgColor: alpha(theme.palette.warning.main, 0.03),
          headerBg: theme.palette.warning.main,
          borderColor: theme.palette.warning.light,
          hoverBg: alpha(theme.palette.warning.main, 0.08),
          accentColor: theme.palette.warning.main,
        };
      case "closed":
        return {
          bgColor: alpha(theme.palette.success.main, 0.03),
          headerBg: theme.palette.success.main,
          borderColor: theme.palette.success.light,
          hoverBg: alpha(theme.palette.success.main, 0.08),
          accentColor: theme.palette.success.main,
        };
      default:
        return {
          bgColor: alpha(theme.palette.primary.main, 0.03),
          headerBg: theme.palette.primary.main,
          borderColor: theme.palette.primary.light,
          hoverBg: alpha(theme.palette.primary.main, 0.08),
          accentColor: theme.palette.primary.main,
        };
    }
  };

  const columnStyles = getColumnStyles();

  // Function to handle card click and pass the job data up
  const handleCardClick = (job: Job) => {
    if (onCardClick) {
      onCardClick(job);
    }
  };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: "1 0 320px",
        minWidth: { xs: "280px", sm: "320px" },
        maxWidth: { xs: "100%", md: "360px" },
        backgroundColor: columnStyles?.bgColor,
        borderRadius: '12px',
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s ease",
        border: isOver && id !== "closed" 
          ? `2px dashed ${columnStyles?.borderColor}` 
          : `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        opacity: isOver && id !== "closed" ? 0.9 : 1,
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        minHeight: "70vh",
        maxHeight: { xs: "60vh", sm: "70vh", md: "80vh" },
        position: "relative",
        overflow: "hidden",
        backdropFilter: "blur(8px)",
        transform: isOver && id !== "closed" ? "scale(1.01)" : "scale(1)",
        ...(isOver && id !== "closed" && {
          "::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: '12px',
            boxShadow: `0 0 0 3px ${columnStyles?.accentColor}`,
            animation: "pulse 1.5s infinite",
            "@keyframes pulse": {
              "0%": { opacity: 0.5 },
              "50%": { opacity: 0.2 },
              "100%": { opacity: 0.5 },
            }
          }
        })
      }}
      aria-label={`${title} column with ${jobs.length} jobs`}
    >
      <Box sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        mb: 1,
        px: 2,
        py: 1.5,
        backgroundImage: `linear-gradient(120deg, ${columnStyles?.headerBg} 0%, ${alpha(columnStyles?.headerBg as string, 0.85)} 100%)`,
        color: "#ffffff", // Use hex code instead of "white"
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: "rgba(255,255,255,0.3)"
        }
      }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            fontSize: { xs: '0.9rem', sm: '1rem' },
            letterSpacing: '0.3px',
            textShadow: "0 1px 2px rgba(0,0,0,0.1)"
          }}
        >
          {title}
        </Typography>
        <Badge 
          badgeContent={jobs.length} 
          color="default"
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: "#ffffff", // Use hex code instead of "white"
              color: columnStyles?.headerBg,
              fontWeight: "bold",
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              minWidth: "22px",
              height: "22px"
            }
          }}
        />
      </Box>
      
      <Box 
        sx={{ 
          p: 2, 
          pt: 1, 
          flex: 1, 
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha("#000000", 0.2)} ${alpha("#000000", 0.05)}`,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.03)",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.15)",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.25)",
            }
          }
        }}
      >
        {jobs.map((job) => (
          <KanbanCard
            key={job.id}
            id={job.id}
            title={job.title}
            applicationsCount={job.applicationsCount}
            deadline={job.deadline}
            reviewStarted={job.reviewStarted}
            closedDate={job.closedDate}
            columnId={id}
            shortlistedCount={job.shortlistedCount}
            finalizedCount={job.finalizedCount}
            onClick={() => handleCardClick(job)}
          />
        ))}
        
        {jobs.length === 0 && (
          <Box 
            sx={{ 
              backgroundColor: alpha("#ffffff", 0.7), // Use hex code instead of "white"
              p: 3,
              borderRadius: 2,
              textAlign: "center",
              opacity: 0.7,
              mt: 2,
              border: `1px dashed ${alpha(theme.palette.divider, 0.6)}`,
              backdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "150px",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: "medium" }}>
              No jobs in this status
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: "80%" }}>
              {id === "open" && "Drag jobs here to reopen them"}
              {id === "inReview" && "Move jobs here when ready for review"}
              {id === "closed" && "Jobs will appear here when finalized"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

KanbanColumn.displayName = "KanbanColumn";